<template>
    <top-navbar
        :app-icon="appIcon"
        employeeName=""
        :page-title="routeTitle"
        :showPath="route.path != '/login'"
        app-title="KPMG Machine Learning Service"
    >
    </top-navbar>
    <side-navbar
        v-if="showSidebar"
        :app-support-url="SUPPORT_URL"
        :app-version="VERSION"
        :app-versions-history="VERSION_HISTORY"
        :menu="appSideNavItemGroups"
        :open="appSideNavOpenItems"
        :visibility-check="visibilityCheck"
        @open-changelog="openChangelog"
    >
        <template #prepend="{ rail }">
            <assigned-application-picker v-if="!rail" />
        </template>
    </side-navbar>
    <k-side-panel :show="showSidePanel" :style="pagePanelWidth"/>
    <v-main class="h-screen pb-2 pr-2 d-flex align-content-stretch">
        <v-card
            :flat="true"
            class="dds-page-container flex-grow-1 p-0 mx-0 overflow-auto"
            elevation="1"
        >
            <router-view/>
        </v-card>
    </v-main>
    <changelog-dialog ref="changelogRef" />
    <dev-toolbar />
</template>

<script lang="ts">
import {ComputedRef, ref, watch} from "vue";
import {computed, defineComponent} from "vue";
import {useRoute} from "vue-router";
import {useAppConst} from "@/composables/useAppConst";
import {IMenuItemGroupLink} from "@/components/dds/SideNavbar.vue";
import {useWindowSize} from "@vueuse/core";
import KToolTip from "@/components/dds/KToolTip/KToolTip.vue";
import {useAuthorizationStore} from "@/stores/AuthorizationStore/AuthorizationStore";
import {storeToRefs} from "pinia";
import ChangelogDialog from "@/components/ChangelogDialog.vue";
import DevToolbar from "@/components/DevToolbar.vue";
import AssignedApplicationPicker from "@/components/picker/AssignedApplicationPicker.vue";

export default defineComponent({
    components: {
        KToolTip,
        ChangelogDialog,
        DevToolbar,
        AssignedApplicationPicker,
    },
    setup() {
        const {width} = useWindowSize();
        const {
            SUPPORT_URL,
            VERSION,
            VERSION_HISTORY,
            MENU,
            showSidePanel,
            sidePanelPageOffset,
            getIcon,
            getTitle,
            ROLES
        } = useAppConst();

        const route = useRoute();
        const authStore = useAuthorizationStore();
        const appSideNavOpenItems = [];
        const showSidebar = ref(true)
        const {roles, services, isAppAdmin} = storeToRefs(authStore)

        const changelogRef = ref<InstanceType<typeof ChangelogDialog> | null>(null)

        function openChangelog() {
            changelogRef.value?.open()
        }

        watch([roles, services, isAppAdmin], ()=>{
            showSidebar.value = false;
            showSidebar.value = true;
        })
        const visibilityCheck = (item: IMenuItemGroupLink) => {
            return true;
        };
        const filterLinksByService = (links: any[]): any[] => {
            return links
                .filter(link => !link.service || authStore.services.includes(link.service))
                .map(link => {
                    if (link.childLinks) {
                        const filtered = filterLinksByService(link.childLinks)
                        return filtered.length > 0 ? {...link, childLinks: filtered} : null
                    }
                    return link
                })
                .filter(Boolean)
        }

        const appSideNavItemGroups = computed(() => {
            const groups = MENU.filter((group) => {
                const groupRole = ROLES.filter(role => role.title === group.role).pop()
                if (authStore.roles.length > 0 && groupRole) {
                    if (authStore.roles.includes(groupRole.value)) {
                        return true
                    }
                }
                return false;
            }).map((group) => {
                return {caption: group.caption, links: filterLinksByService(group.links)};
            });

            // Deduplicate: if ADMIN group already contains a link, remove it from APP_ADMIN
            const adminValues = new Set<string>()
            groups.forEach(g => {
                // Collect all link values from groups that originally came from ADMIN role
                const sourceGroup = MENU.find(m => m.links === g.links || filterLinksByService(m.links).length === g.links.length)
                g.links.forEach(link => {
                    adminValues.add(link.value)
                    if (link.childLinks) {
                        link.childLinks.forEach((cl: any) => adminValues.add(cl.value))
                    }
                })
            })
            // Remove groups that end up with no links after dedup
            return groups.map(g => ({
                ...g,
                links: g.links.filter(link => {
                    // Keep all links from groups that have > 1 link (ADMIN, USER)
                    // Only deduplicate from single-link groups (APP_ADMIN) if the link
                    // already appears in a multi-link group
                    if (groups.filter(og => og !== g).some(og =>
                        og.links.some(ol => ol.value === link.value) ||
                        og.links.some(ol => ol.childLinks?.some((cl: any) => cl.value === link.value))
                    )) {
                        return false
                    }
                    return true
                })
            })).filter(g => g.links.length > 0);
        });

        const appIcon = computed(() => {
            if (route) {
                return (
                    appSideNavItemGroups.value.reduce((icon, group): string => {
                        return getIcon(group.links, icon);
                    }, null) ??
                    (route?.meta && route?.meta?.icon
                        ? (route.meta.icon as string)
                        : null) ??
                    "mdi-finance"
                );
            }
            return "";
        });

        const routeTitle: ComputedRef<string> = computed(() => {
            if (route) {
                return (
                    appSideNavItemGroups.value.reduce((title, group) => {
                        return getTitle(group.links, title);
                    }, null) ??
                    (route?.meta && route?.meta?.title
                        ? (route.meta.title as string)
                        : null) ??
                    "?"
                );
            }
            return "";
        });

        const pagePanelWidth = computed(() => {
            return width.value > 1400 ? {width: "416px"} : {width: "0"};
        });

        return {
            width,
            //action
            visibilityCheck,
            // const
            appIcon,
            SUPPORT_URL,
            VERSION,
            VERSION_HISTORY,
            appSideNavOpenItems,
            appSideNavItemGroups,
            // ref
            showSidePanel,
            pagePanelWidth,
            showSidebar,
            // computed
            routeTitle,
            sidePanelPageOffset,
            route,
            changelogRef,
            openChangelog,

        };
    },
});
</script>

<style lang="scss" scoped>
.dds-page-container {
    margin-right: v-bind(sidePanelPageOffset) !important;
}

div:deep(.v-application--wrap) {
    min-height: calc(100vh - 65px) !important;
}
</style>
